import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Clock, Calendar } from 'lucide-react';

const Pricing = () => {
  useEffect(() => {
    document.title = 'Pricing - Pin High Golf & Lounge | Bay Rates & Senior Discounts';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Simulator bay rates at Pin High Golf & Lounge. Hourly rentals from $30, with senior rates from $20. Book your bay in Bay Village, Ohio.');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/pricing');
    updateSocialMeta({
      title: 'Pricing - Pin High Golf & Lounge | Bay Rates & Senior Discounts',
      description: 'Simulator bay rates at Pin High Golf & Lounge. Hourly rentals from $30, with senior rates from $20. Book your bay in Bay Village, Ohio.',
      path: '/pricing',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
              Bay Rental Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Play on world-class golf courses with our state-of-the-art Foresight simulators. Hourly rates designed to fit your schedule and budget.
            </p>
            <p className="text-sm font-semibold uppercase tracking-wide text-green-700 mb-6">
              Rates effective Tuesday, September 8
            </p>
            <Button
              asChild
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300"
            >
              <Link to="/courses">
                Course List
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Weekday Rates */}
            <Card className="border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-green-50 to-white border-b">
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-green-600" />
                  Monday - Thursday
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Morning/Afternoon</p>
                      <p className="text-sm text-gray-600">9:00 AM - 4:00 PM</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$30</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Evening</p>
                      <p className="text-sm text-gray-600">4:00 PM - Close</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$40</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Senior Rate (60+)
                    </p>
                    <p className="text-sm text-blue-800 mb-2">9:00 AM - 4:00 PM</p>
                    <p className="text-2xl font-bold text-blue-900">$20/hour</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Friday Rates */}
            <Card className="border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-green-50 to-white border-b">
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-green-600" />
                  Friday
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Morning/Afternoon</p>
                      <p className="text-sm text-gray-600">9:00 AM - 4:00 PM</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$30</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Senior Rate (60+)
                    </p>
                    <p className="text-sm text-blue-800 mb-2">9:00 AM - 4:00 PM</p>
                    <p className="text-2xl font-bold text-blue-900">$20/hour</p>
                  </div>
                  <div className="flex justify-between items-center pb-4">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Evening</p>
                      <p className="text-sm text-gray-600">4:00 PM - Close</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$50</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weekend Rates */}
            <Card className="border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-green-50 to-white border-b">
                <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-green-600" />
                  Saturday & Sunday
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Saturday Morning/Afternoon</p>
                      <p className="text-sm text-gray-600">10:00 AM - 4:00 PM</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$40</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Saturday Evening</p>
                      <p className="text-sm text-gray-600">4:00 PM - Close</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$50</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pb-4">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Sunday All Day</p>
                      <p className="text-sm text-gray-600">10:00 AM - 9:00 PM</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">$40</p>
                      <p className="text-sm text-gray-600">/hour</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* What's Included */}
          <Card className="border-none shadow-lg bg-gradient-to-br from-gray-50 to-white">
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-gray-900 text-center">
                What's Included With Every Bay Rental
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Foresight Technology</h3>
                    <p className="text-sm text-gray-600">Professional-grade launch monitors used by PGA Tour pros</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">200+ Golf Courses</h3>
                    <p className="text-sm text-gray-600">Play world-famous courses from Pebble Beach to St. Andrews</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Comfortable Bays</h3>
                    <p className="text-sm text-gray-600">Climate-controlled private bays with seating for your group</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Practice Modes</h3>
                    <p className="text-sm text-gray-600">Driving range, closest-to-pin, and skills challenges</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Club Rentals</h3>
                    <p className="text-sm text-gray-600">Complimentary club rentals available upon request</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Food & Beverage</h3>
                    <p className="text-sm text-gray-600">Full bar and menu service delivered to your bay</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Membership Callout */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Play More, Save More
          </h2>
          <p className="text-xl mb-8 text-green-50">
            Frequent player? Our membership plans offer significant savings, priority booking, and exclusive benefits.
          </p>
          <Button 
            asChild
            size="lg" 
            className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105"
          >
            <a href="/memberships">Explore Memberships</a>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Book Your Bay?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Reserve your tee time now and experience the best indoor golf in Bay Village.
          </p>
          <Button 
            asChild
            size="lg" 
            className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105"
          >
            <a href="https://app.whoosh.io/patron/club/pin-high-golf-lounge/agenda/pin-high/today" target="_blank" rel="noopener noreferrer">
              Book Now
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;